import { cloneDeep } from 'lodash-es'
import { z } from 'zod'

import { type CoverData, DEFAULT_SALES_PRICE_COVER_DATA_ITEM } from '~/@entities/DesignTemplate'
import { fromProblemV2, ProblemV2WireSchema } from '~/@entities/Problem'
import type {
  CommonCloudFrontUrlResponse,
  CommonResponseCollectionWorkbookAssignSearchResponse,
  CommonResponseWorkbookWithFilterDto,
  CustomSignatureWorkbookCreationResponse,
  CustomSignatureWorkbookFilterCreationResponse,
  CustomSignatureWorkbookFilterResponseV3,
  SliceWorkbookSearchResponse,
  WorkbookPageResponse,
  WorkbookPageVo,
} from '~/api-spec/__generated__/main'
import { parseApiResponse } from '~/schema/apiBoundary'
import { mapSliceContent, type Slice, sliceSchema } from '~/schema/slice'

import {
  type ListedWorkbook,
  ListedWorkbookSchema,
  type SignatureWorkbookDraft,
  SignatureWorkbookDraftSchema,
  type Workbook,
  type WorkbookAssignedStudent,
  WorkbookAssignedStudentSchema,
  WorkbookAssigneeSchema,
  type WorkbookDetail,
  WorkbookDetailSchema,
  type WorkbookFile,
  WorkbookFileSchema,
  type WorkbookListItem,
  type WorkbookPageImage,
  WorkbookPageImageSchema,
  WorkbookPageProblemSchema,
  type WorkbookPageWithProblems,
  WorkbookPageWithProblemsSchema,
  type WorkbookProblem,
  WorkbookProblemSchema,
  WorkbookProblemXistorySchema,
} from './schemas'

const ENTITY = 'Workbook'

// 서버의 교재 응답(WorkbookDto)은 종류마다 하위 클래스(PublicWorkbookDto·CustomSignatureWorkbookDto 등 7개)로
// 직렬화되는데 스펙은 상위 클래스만 그려 종류별 필드(signatureType·featureType·coverData·academyGroupId·
// thumbnailImageUrl·pairFlag 등)가 없다. 목록 줄의 list 도 런타임 클래스(StudentWorkbookWithNameAndStatusDto)의
// studentName·studentStatus 가 빠져 있다. 교재 문제·페이지(WorkbookProblemResponse·WorkbookPageResponse)도 같은 이유로
// problemId·xistory·answerConfirmed·imageUrl 이 없고, nullable 필드를 optional 로만 그린다.
// 2026-10-08 백엔드 develop 의 workbook/model/dto/*WorkbookDto.kt·workbook/controller/response/
// WorkbookSearchResponse.kt·WorkbookPageResponse.kt·WorkbookProblemResponse.kt 로 확인.
const asWire = (raw: unknown): unknown => raw

// 교재 가격을 배포하기 전에 만든 표지에는 판매가 칸이 없어 기본값을 채운다
export const withDefaultSalesPrice = (coverData: CoverData | null): CoverData | null =>
  coverData && {
    ...coverData,
    salesPrice: coverData.salesPrice ?? cloneDeep(DEFAULT_SALES_PRICE_COVER_DATA_ITEM),
  }

// 검증 스키마에 transform 을 두면 lenient 모드가 검증에 실패한 raw 를 넘길 때 모양 바꾸기까지 빠진다.
// 그래서 검증과 모양 바꾸기를 나눠, 모양 바꾸기는 parseApiResponse 결과에 일반 함수로 적용한다.

/** 다른 응답(학생 교재 조회 등)이 품고 온 교재에 표지 기본값을 채운다 */
export const withWorkbookCoverDefaults = <W extends Workbook>(workbook: W): W =>
  'coverData' in workbook
    ? { ...workbook, coverData: withDefaultSalesPrice(workbook.coverData ?? null) }
    : workbook

// 목록 줄은 교재 필드가 펼쳐진(@JsonUnwrapped) 채로 오고 배정 정보가 옆에 붙는다
const _WorkbookListItemWireSchema = z.intersection(
  ListedWorkbookSchema,
  z.object({
    list: z.array(WorkbookAssigneeSchema),
    assigned: z.boolean().nullable(),
    lastAssignDateTime: z.iso.datetime({ local: true }).nullable(),
    lastUsePageNumber: z.number().nullable(),
  }),
)

const toWorkbookListItem = ({
  list,
  assigned,
  lastAssignDateTime,
  lastUsePageNumber,
  ...workbook
}: z.output<typeof _WorkbookListItemWireSchema>): WorkbookListItem => {
  const listed = withWorkbookCoverDefaults(workbook as ListedWorkbook)
  return {
    id: listed.id,
    workbook: listed,
    list: list ?? [],
    assigned,
    lastAssignDateTime,
    lastUsePageNumber,
  }
}

// 서버는 정답 칸 수(answerCount)를 교재 페이지 응답에는 싣지 않는다. 학생 채점 응답과 같은 규칙
// (정답 문자열을 쉼표로 나눈 비지 않은 칸의 수, WorkbookProblemDto.answerCount)으로 채운다.
const countAnswers = (answer: string | undefined) =>
  typeof answer === 'string' ? answer.split(',').filter((part) => part.trim() !== '').length : 0

// 교재 문제가 품는 문제은행 문제(엑스토리·내 교재 문제)는 V2 모양(ProblemDto)으로 온다
const _WorkbookProblemV2Schema = WorkbookProblemSchema.extend({
  xistory: WorkbookProblemXistorySchema.extend({ problem: ProblemV2WireSchema.nullable() })
    .nullable()
    .optional(),
  problem: ProblemV2WireSchema.nullable().optional(),
})

/** 다른 응답(학생 채점 조회)이 품고 오는 교재 문제. 검증 뒤 toWorkbookProblem 으로 V2 문제를 푼다 */
export const WorkbookProblemWireSchema = _WorkbookProblemV2Schema

type _WorkbookProblemV2Parts = Partial<
  Pick<z.output<typeof _WorkbookProblemV2Schema>, 'xistory' | 'problem'>
>

/** 교재 문제가 품은 V2 문제(엑스토리·내 교재 문제)를 도메인 모양으로 푼다. null·undefined 구분은 그대로 둔다 */
export const toWorkbookProblem = <P extends _WorkbookProblemV2Parts>({
  xistory,
  problem,
  ...rest
}: P): Omit<P, 'xistory' | 'problem'> & Pick<WorkbookProblem, 'xistory' | 'problem'> => ({
  ...rest,
  ...(xistory !== undefined && {
    xistory: xistory && {
      ...xistory,
      problem: xistory.problem ? fromProblemV2(xistory.problem) : null,
    },
  }),
  ...(problem !== undefined && { problem: problem ? fromProblemV2(problem) : null }),
})

const _WorkbookPageWithProblemsWireSchema = WorkbookPageWithProblemsSchema.extend({
  pageProblems: z.array(
    WorkbookPageProblemSchema.extend({
      workbookProblem: _WorkbookProblemV2Schema.omit({ answerCount: true }),
    }),
  ),
})

const toWorkbookPageWithProblems = (
  page: z.output<typeof _WorkbookPageWithProblemsWireSchema>,
): WorkbookPageWithProblems => ({
  ...page,
  pageProblems: (page.pageProblems ?? []).map((pageProblem) => ({
    ...pageProblem,
    workbookProblem: {
      ...toWorkbookProblem(pageProblem.workbookProblem ?? {}),
      answerCount: countAnswers(pageProblem.workbookProblem?.answer),
    },
  })),
})

export const adaptWorkbookList = (
  raw: SliceWorkbookSearchResponse | undefined,
): Slice<WorkbookListItem> =>
  mapSliceContent(
    parseApiResponse(sliceSchema(_WorkbookListItemWireSchema), asWire(raw), ENTITY),
    toWorkbookListItem,
  )

export const adaptWorkbookPagesWithProblems = (
  raw: WorkbookPageResponse[],
): WorkbookPageWithProblems[] =>
  (parseApiResponse(z.array(_WorkbookPageWithProblemsWireSchema), asWire(raw), ENTITY) ?? []).map(
    toWorkbookPageWithProblems,
  )

export const adaptWorkbookPageImages = (raw: WorkbookPageVo[] | undefined): WorkbookPageImage[] =>
  parseApiResponse(z.array(WorkbookPageImageSchema), raw, ENTITY)

/***************** 커스텀 시그니처 교재 만들기 *****************/

// 스펙은 임시 필터의 nullable 필드(headtitle·subtitle·academyName·로고 URL)를 optional 로만 그린다.
// 2026-10-08 백엔드 develop 의 workbook/controller/response/CustomSignatureWorkbookFilterResponseV3.kt 로 확인.
export const adaptSignatureWorkbookDraft = (
  raw: CustomSignatureWorkbookFilterResponseV3,
): SignatureWorkbookDraft => parseApiResponse(SignatureWorkbookDraftSchema, asWire(raw), ENTITY)

export const adaptSignatureWorkbookDraftId = (
  raw: CustomSignatureWorkbookFilterCreationResponse,
): SignatureWorkbookDraft['filterId'] =>
  parseApiResponse(z.object({ filterId: z.string() }), raw, ENTITY).filterId

export const adaptCreatedWorkbookId = (
  raw: CustomSignatureWorkbookCreationResponse,
): Workbook['id'] => parseApiResponse(z.object({ workbookId: z.number() }), raw, ENTITY).workbookId

/***************** 교재 상세(선생님 앱) *****************/

// 스펙은 WorkbookWithFilterDto 를 교재를 workbook 으로 품은 모양으로 그리지만, 서버 클래스는 WorkbookDto 를 상속해
// 공통부를 펼쳐 싣고 종류별 값(pairFlag·signatureWorkbookId·signatureType·coverData·innerDesignTemplateId)은
// 해당 종류가 아니면 null 이다. 2026-10-14 백엔드 develop(6a0f8e7bb) 의 workbook/model/dto/WorkbookWithFilterDto.kt 로 확인.
export const adaptWorkbookDetail = (
  raw: CommonResponseWorkbookWithFilterDto['data'],
): WorkbookDetail => {
  const detail = parseApiResponse(WorkbookDetailSchema, asWire(raw), ENTITY)
  return detail && { ...detail, coverData: withDefaultSalesPrice(detail.coverData ?? null) }
}

export const adaptWorkbookAssignedStudents = (
  raw: CommonResponseCollectionWorkbookAssignSearchResponse['data'],
): WorkbookAssignedStudent[] =>
  parseApiResponse(z.array(WorkbookAssignedStudentSchema), raw, ENTITY)

export const adaptWorkbookFile = (raw: CommonCloudFrontUrlResponse): WorkbookFile =>
  parseApiResponse(WorkbookFileSchema, raw, ENTITY)
